import { Button, ButtonV2, Checkbox, Toggle, TooltipV2 } from '@cimplico/components';
import { WorkpaperExport, WorkpaperExportEnum, WorkpaperStatusEnum } from '../../types/workpaper';
import { ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toastNotification } from '../../components/toast/Toast';
import { useUpdateWorkpaperExport } from '../../queries/use-update-workpaper-export';
import { downloadDocument } from '../../utilities/document-download';
import { useDownloadExportUrl } from '../../queries/use-download-export-url';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import ExportWorkpaperDialog from '../dashboard/complete/ExportWorkpaperDialog';
import { useFirmContext } from '../../providers/FirmProvider';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { useIsWorkpaperRole } from '../../hooks/use-is-workpaper-role';
import { calcRelativeTime, formatDateTime } from '../../utilities/formatting';
import { useHasFeatureAccess } from '../../hooks/use-has-feature-access';

type FeatureName =
    | 'excluded'
    | 'wsChecklists'
    | 'wsJournals'
    | 'wsDraftJournals'
    | 'wsPostedJournals'
    | 'wsVoidedJournals'
    | 'wsArchivedJournals'
    | 'wsFiles'
    | 'wsComments'
    | 'wsQueries'
    | 'osFiles'
    | 'osComments'
    | 'osQueries'
    | 'draftJournals'
    | 'postedJournals'
    | 'voidedJournals'
    | 'archivedJournals';

type ExportConfig = {
    title: string;
    name: 'trialBalance' | 'files' | 'procedures' | 'worksheets' | 'otherSchedules' | 'journals';
    description: ReactNode;
    features: {
        title: string;
        description: string;
        name: FeatureName;
        subFeatures?: { title: string; description: string; name: FeatureName }[];
    }[];
};

type EditExportConfigFormProps = {
    workpaperExport: WorkpaperExport;
};

function EditExportConfigForm({ workpaperExport }: EditExportConfigFormProps): JSX.Element {
    const { status, name, id } = useWorkpaperContext();
    const { isGuest } = useIsWorkpaperRole();

    const canEditWorkpaperExport = useHasFeatureAccess('workpaper-export:write');

    const updateWorkpaperExportQuery = useUpdateWorkpaperExport(id);
    const downloadExportQuery = useDownloadExportUrl(id);

    const [action, setAction] = useState<string | undefined>();

    const config: ExportConfig[] = [
        {
            title: 'Procedures',
            name: 'procedures',
            description: <p className="text-gray-500 font-normal">Would you like to include procedures in the export?</p>,
            features: [],
        },
        {
            title: 'Trial Balance',
            name: 'trialBalance',
            description: (
                <p className="text-gray-500 font-normal">
                    Would you like to include the trial balance in the export? Use the below checkboxes to configure your trial balance
                    inclusion preferences.
                </p>
            ),
            features: [
                {
                    title: 'Excluded',
                    name: 'excluded',
                    description: 'Include accounts marked as “Excluded”.',
                },
            ],
        },
        {
            title: 'Worksheets',
            name: 'worksheets',
            description: (
                <p className="text-gray-500 font-normal">
                    Would you like to include worksheets in the export? Use the below checkboxes to configure your worksheet inclusion
                    preferences.
                </p>
            ),
            features: [
                {
                    title: 'Checklists',
                    name: 'wsChecklists',
                    description: 'Include completed and not applicable checklists on the worksheet (if applicable).',
                },
                {
                    title: 'Journals',
                    name: 'wsJournals',
                    description: 'Include journals made on a worksheet.',
                    subFeatures: [
                        {
                            title: 'Draft',
                            name: 'wsDraftJournals',
                            description: 'Include journals marked as “Draft”.',
                        },
                        {
                            title: 'Posted',
                            name: 'wsPostedJournals',
                            description: 'Include journals marked as “Posted”.',
                        },
                        {
                            title: 'Voided',
                            name: 'wsVoidedJournals',
                            description: 'Include journals marked as “Voided”.',
                        },
                        {
                            title: 'Archived',
                            name: 'wsArchivedJournals',
                            description: 'Include journals marked as “Archived”.',
                        },
                    ],
                },
                {
                    title: 'Files',
                    name: 'wsFiles',
                    description: 'Include referenced and unreferenced files saved on a worksheet.',
                },
                {
                    title: 'Internal Comments',
                    name: 'wsComments',
                    description: 'Include internal comments made on a worksheet.',
                },
                {
                    title: 'Client Queries',
                    name: 'wsQueries',
                    description: 'Include client queries made on a worksheet.',
                },
            ],
        },
        {
            title: 'Other Schedules',
            name: 'otherSchedules',
            description: (
                <p className="text-gray-500 font-normal">
                    Would you like to include other schedules in the export? Use the below checkboxes to configure your other schedules
                    inclusion preferences.
                </p>
            ),
            features: [
                {
                    title: 'Files',
                    name: 'osFiles',
                    description: 'Include referenced and unreferenced files saved on a schedule.',
                },
                {
                    title: 'Internal Comments',
                    name: 'osComments',
                    description: 'Include internal comments made on a schedule.',
                },
                {
                    title: 'Client Queries',
                    name: 'osQueries',
                    description: 'Include client queries made on a schedule.',
                },
            ],
        },
        {
            title: 'Journals',
            name: 'journals',
            description: (
                <p className="text-gray-500 font-normal">
                    Would you like to include journals in the export? Use the below checkboxes to configure your journals inclusion
                    preferences.
                </p>
            ),
            features: [
                {
                    title: 'Draft',
                    name: 'draftJournals',
                    description: 'Include journals marked as “Draft”.',
                },
                {
                    title: 'Posted',
                    name: 'postedJournals',
                    description: 'Include journals marked as “Posted”.',
                },
                {
                    title: 'Voided',
                    name: 'voidedJournals',
                    description: 'Include journals marked as “Voided”.',
                },
                {
                    title: 'Archived',
                    name: 'archivedJournals',
                    description: 'Include journals marked as “Archived”.',
                },
            ],
        },
        {
            title: 'Files',
            name: 'files',
            description: (
                <p className="text-gray-500 font-normal">
                    Would you like to include unreferenced files stored in the <b>{name}</b> folder in the export?
                </p>
            ),
            features: [],
        },
    ];

    const { procedures, trialBalance, worksheets, otherSchedules, journals, files } = workpaperExport.config;

    const { getValues, control, setValue } = useForm({
        defaultValues: {
            procedures,
            trialBalance: trialBalance.show,
            worksheets: worksheets.show,
            otherSchedules: otherSchedules.show,
            journals: journals.show,
            files,
            excluded: trialBalance.excluded,
            wsChecklists: worksheets.checklists,
            wsJournals: worksheets.journals.show,
            wsDraftJournals: worksheets.journals.draft,
            wsPostedJournals: worksheets.journals.posted,
            wsVoidedJournals: worksheets.journals.voided,
            wsArchivedJournals: worksheets.journals.archived,
            wsFiles: worksheets.files,
            wsComments: worksheets.comments,
            wsQueries: worksheets.queries,
            osFiles: otherSchedules.files,
            osComments: otherSchedules.comments,
            osQueries: otherSchedules.queries,
            draftJournals: journals.draft,
            postedJournals: journals.posted,
            voidedJournals: journals.voided,
            archivedJournals: journals.archived,
        },
        reValidateMode: 'onBlur',
    });

    function handleToggleChange(
        enabled: boolean,
        name: 'trialBalance' | 'journals' | 'files' | 'procedures' | 'worksheets' | 'otherSchedules'
    ): void {
        setValue(name, enabled);

        if (name === 'journals' && enabled) {
            const isAllJournalsUnchecked =
                !getValues('draftJournals') &&
                !getValues('postedJournals') &&
                !getValues('voidedJournals') &&
                !getValues('archivedJournals');

            // Check all subfilters if main journal toggle is on and all subfilters were unchecked previously
            if (isAllJournalsUnchecked) {
                setValue('draftJournals', true);
                setValue('postedJournals', true);
                setValue('voidedJournals', true);
                setValue('archivedJournals', true);
            }
        }

        onSubmit();
    }

    function handleCheckboxChange(
        enabled: boolean,
        name:
            | 'excluded'
            | 'wsChecklists'
            | 'wsFiles'
            | 'wsComments'
            | 'wsQueries'
            | 'wsJournals'
            | 'wsDraftJournals'
            | 'wsPostedJournals'
            | 'wsVoidedJournals'
            | 'wsArchivedJournals'
            | 'osFiles'
            | 'osComments'
            | 'osQueries'
            | 'draftJournals'
            | 'postedJournals'
            | 'voidedJournals'
            | 'archivedJournals'
    ): void {
        setValue(name, enabled);

        if (['wsJournals', 'wsDraftJournals', 'wsPostedJournals', 'wsVoidedJournals', 'wsArchivedJournals'].includes(name)) {
            const isAllWsJournalsUnchecked =
                !getValues('wsDraftJournals') &&
                !getValues('wsPostedJournals') &&
                !getValues('wsVoidedJournals') &&
                !getValues('wsArchivedJournals');

            // Check all subfilters if main worksheet journals checkbox is checked and all subfilters were unchecked previously
            if (name === 'wsJournals' && enabled && isAllWsJournalsUnchecked) {
                setValue('wsDraftJournals', true);
                setValue('wsPostedJournals', true);
                setValue('wsVoidedJournals', true);
                setValue('wsArchivedJournals', true);
            }

            // Uncheck main worksheet journals checkbox if all subfilters are unchecked
            if (name !== 'wsJournals' && isAllWsJournalsUnchecked) {
                setValue('wsJournals', false);
            }
        }

        if (['draftJournals', 'postedJournals', 'voidedJournals', 'archivedJournals'].includes(name)) {
            const isAllJournalsUnchecked =
                !getValues('draftJournals') &&
                !getValues('postedJournals') &&
                !getValues('voidedJournals') &&
                !getValues('archivedJournals');

            // Uncheck main journals checkbox if all subfilters are unchecked
            if (isAllJournalsUnchecked) {
                setValue('journals', false);
            }
        }

        onSubmit();
    }

    function onSubmit(): void {
        const {
            procedures,
            trialBalance,
            worksheets,
            otherSchedules,
            journals,
            files,
            excluded,
            wsChecklists,
            wsJournals,
            wsDraftJournals,
            wsPostedJournals,
            wsVoidedJournals,
            wsArchivedJournals,
            wsFiles,
            wsComments,
            wsQueries,
            osFiles,
            osComments,
            osQueries,
            draftJournals,
            postedJournals,
            voidedJournals,
            archivedJournals,
        } = getValues();

        updateWorkpaperExportQuery.mutate(
            {
                config: {
                    procedures,
                    trialBalance: { show: trialBalance, excluded },
                    worksheets: {
                        show: worksheets,
                        checklists: wsChecklists,
                        journals: {
                            show: wsJournals,
                            draft: wsDraftJournals,
                            posted: wsPostedJournals,
                            voided: wsVoidedJournals,
                            archived: wsArchivedJournals,
                        },
                        files: wsFiles,
                        comments: wsComments,
                        queries: wsQueries,
                    },
                    otherSchedules: { show: otherSchedules, files: osFiles, comments: osComments, queries: osQueries },
                    journals: {
                        show: journals,
                        draft: draftJournals,
                        posted: postedJournals,
                        voided: voidedJournals,
                        archived: archivedJournals,
                    },
                    files,
                },
            },
            {
                // onSuccess: () => {
                //     toastNotification('success', 'Successfully updated configuration!');
                // },
                onError: () => {
                    toastNotification('error', 'Unable to update configuration!');
                },
            }
        );
    }

    async function downloadExport(): Promise<void> {
        const result = await downloadExportQuery.refetch();
        if (result.data?.downloadUrl) {
            await downloadDocument(id, result.data.downloadUrl);
            toastNotification('success', 'Workpaper export downloaded');
        } else if (result.status === 'error') {
            toastNotification('error', result.error.response?.data.message);
        }
    }

    return (
        <>
            <ExportWorkpaperDialog isOpen={action === 'EXPORT'} onClose={(): void => setAction(undefined)} workpaperId={id} />
            <div className="mb-4">
                {(workpaperExport.status === WorkpaperExportEnum.NOT_EXPORTED || workpaperExport.status === WorkpaperExportEnum.ERROR) && (
                    <div className="w-fit">
                        <ButtonV2
                            type="primary"
                            label="Export"
                            size="sm"
                            isDisabled={status !== WorkpaperStatusEnum.COMPLETE}
                            tooltipPosition="right"
                            disabledHint={
                                status !== WorkpaperStatusEnum.COMPLETE
                                    ? 'This workpaper is still active. Only workpapers marked as completed can be exported.'
                                    : undefined
                            }
                            onClick={(): void => setAction('EXPORT')}
                        />
                    </div>
                )}
                {workpaperExport.status === WorkpaperExportEnum.EXPORTING && (
                    <ButtonV2
                        type="primary"
                        label="Exporting..."
                        size="sm"
                        isDisabled
                        isLoading={workpaperExport.status === WorkpaperExportEnum.EXPORTING}
                    />
                )}
                {workpaperExport.status === WorkpaperExportEnum.EXPORTED && (
                    <div className="flex gap-4 items-center">
                        <ButtonV2
                            type="primary"
                            label={`${downloadExportQuery.isLoading ? 'Downloading...' : 'Download'}`}
                            size="sm"
                            isDisabled={downloadExportQuery.isLoading}
                            isLoading={downloadExportQuery.isLoading}
                            onClick={downloadExport}
                        />
                        {workpaperExport.exportedAt && (
                            <TooltipV2 message={formatDateTime(workpaperExport.exportedAt)}>
                                <p className="text-gray-700 text-sm">{calcRelativeTime(workpaperExport.exportedAt)}</p>
                            </TooltipV2>
                        )}
                    </div>
                )}
            </div>

            <div className="max-w-[600px] border rounded-md pt-4 px-4">
                <h3 className="font-bold text-gray-900 my-2 text-base">Export Settings</h3>
                <p className="text-gray-500 text-sm font-normal">
                    Toggle and select the content you would like to be included in the export for this workpaper.
                </p>

                <div className="relative">
                    {!canEditWorkpaperExport && <Preview />}
                    <div className={`mt-12 ${!canEditWorkpaperExport ? 'blur-sm' : ''}`}>
                        {config.map((item, idx) => {
                            return (
                                <div key={idx} className="pb-8">
                                    <div className="flex gap-16 justify-between">
                                        <div className="text-sm">
                                            <p className="font-medium text-gray-900">{item.title}</p>
                                            {item.description}
                                        </div>
                                        <Controller
                                            name={item.name}
                                            control={control}
                                            render={({ field: { value } }): JSX.Element => (
                                                <Toggle
                                                    onToggle={(val): void => handleToggleChange(val, item.name)}
                                                    type="short"
                                                    defaultEnabled={value}
                                                    isDisabled={
                                                        !canEditWorkpaperExport ||
                                                        isGuest ||
                                                        workpaperExport.status === WorkpaperExportEnum.EXPORTING
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                    {item.features.map((f, i) => {
                                        return (
                                            <div key={i}>
                                                <div className="flex gap-3 items-start pt-2">
                                                    <div className="pt-0.5">
                                                        <Controller
                                                            name={f.name}
                                                            control={control}
                                                            render={({ field: { value } }): JSX.Element => (
                                                                <Checkbox
                                                                    checked={value}
                                                                    onChange={(): void => handleCheckboxChange(!getValues(f.name), f.name)}
                                                                    disabled={
                                                                        !canEditWorkpaperExport ||
                                                                        isGuest ||
                                                                        !getValues(item.name) ||
                                                                        workpaperExport.status === WorkpaperExportEnum.EXPORTING
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="text-xs">
                                                        <p className="font-medium text-gray-900">{f.title}</p>
                                                        <p className="text-gray-500 font-normal">{f.description}</p>
                                                    </div>
                                                </div>
                                                {f.subFeatures?.map((sub, i) => {
                                                    return (
                                                        <div className="inline-block pt-2 pl-7" key={i}>
                                                            <div className="flex gap-2 items-center ">
                                                                <Controller
                                                                    name={sub.name}
                                                                    control={control}
                                                                    render={({ field: { value } }): JSX.Element => (
                                                                        <Checkbox
                                                                            checked={value}
                                                                            onChange={(): void =>
                                                                                handleCheckboxChange(!getValues(sub.name), sub.name)
                                                                            }
                                                                            disabled={
                                                                                !canEditWorkpaperExport ||
                                                                                isGuest ||
                                                                                !getValues(f.name) ||
                                                                                workpaperExport.status === WorkpaperExportEnum.EXPORTING
                                                                            }
                                                                        />
                                                                    )}
                                                                />
                                                                <p className="text-xs text-gray-900">{sub.title}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
}

function Preview(): JSX.Element {
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const { isAdmin } = useIsFirmRole();

    return (
        <div className="flex flex-col justify-center items-center absolute z-40 bottom-[90%] right-1/2 translate-x-1/2 translate-y-1/2 bg-white/90 py-4 px-6 rounded-md shadow-md w-[80%]">
            <LockClosedIcon className="h-6 w-6 text-gray-600" />
            <p className="text-gray-600 mb-2 text-center">
                Upgrade to the <b>Essential</b> or <b>Professional</b> plan to access this feature
            </p>

            <Button
                type="primary"
                size="sm"
                label="Upgrade"
                leftIcon={<ArrowUpCircleIcon />}
                onClick={(): void => {
                    navigate(`/${shortId}/settings`);
                }}
                disabled={!isAdmin}
            />

            {!isAdmin && <p className="text-gray-600 mt-2 text-sm">To upgrade, please contact your firm&apos;s administrator</p>}
        </div>
    );
}
export default EditExportConfigForm;
